<script setup>
import {
  AudioWaveform,
  BookOpen,
  Bot,
  Command,
  Frame,
  GalleryVerticalEnd,
  Map,
  PieChart,
  Settings2,
  MapPin,
  Import,
  LockOpen,
  Logs,
  ClipboardCheck,
  UsersRound,
  Pyramid,
  SquareChartGantt,
  CircleUserRound,
  Settings,
  Home,
  FileSpreadsheet,
} from "lucide-vue-next";
import {
  UserStar,
} from "lucide-react";
import NavMain from '@/components/NavMain.vue';
import NavProjects from '@/components/NavProjects.vue';
import NavUser from '@/components/NavUser.vue';
import TeamSwitcher from '@/components/TeamSwitcher.vue';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
} from '@/components/ui/sidebar';

import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar'

import RechtsgrundlagenLogo from '@/components/images/rechtsgrundlagen.svg'
import PflichtenLogo from '@/components/images/pflichten.svg'
import DangeroursMaterialsLogo from '@/components/images/biohazard.svg'
import KundeSelect from './KundeSelect.vue'
import StandortSelect from './StandortSelect.vue'
import GlobalSearch from './GlobalSearch.vue'
import LanguageSwitcher from './LanguageSwitcher.vue'

import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useViewStore } from '@/stores/viewStore'
import { get_data_attr } from '@/utils/globals'

const viewStore = useViewStore();

const props = defineProps({
  side: { type: String, required: false },
  variant: { type: String, required: false },
  collapsible: { type: String, required: false, default: "icon" },
  class: { type: null, required: false },
  actionView: String,
});

const emit = defineEmits(['updateView']);

const { t } = useI18n();

// This is sample data.
const data = {
  user: {
    name: "shadcn",
    email: "m@example.com",
    avatar: "/avatars/shadcn.jpg",
  },
};

const user = ref(get_data_attr("user"));
const customer = ref(get_data_attr("customer"));
const standorts = ref(get_data_attr("standorts"));
const hasManyStandorts = ref(standorts.value.filter(s => s.value >= 0).length > 0)

// Navigation items (reactive to locale changes)
const navMain = computed(() => [
  {
    title: t('view.dashboard'),
    url: "registry.dashboard",
    icon: PieChart,
    is_authorized: customer.value.is_registry
  },
  {
    title: t('view.rechtsgrundlagen'),
    url: "registry.rechtsgrundlagen",
    icon: RechtsgrundlagenLogo,
    is_authorized: customer.value.is_registry
  },
  {
    title: t('view.pflichten'),
    url: "registry.pflichten",
    icon: PflichtenLogo,
    is_authorized: customer.value.is_registry
  },
  {
    title: t('view.tasks'),
    url: "registry.tasks",
    icon: ClipboardCheck,
    is_authorized: customer.value.is_registry
  },
	{
	  title: t('view.logs'),
	  url: "registry.logs",
	  icon: Logs,
    is_authorized: customer.value.is_registry
	},
	{
	  title: t('view.excel'),
	  url: "registry.excel",
	  icon: FileSpreadsheet,
    is_authorized: user.value.is_excel && customer.value.is_registry
	},
  {
	  title: t('view.gefahrstoffe'),
	  url: "registry.gefahrstoffe",
	  icon: DangeroursMaterialsLogo,
    is_authorized: customer.value.is_dangergoods_registry
	},
	{
	  title: t('view.customers'),
	  url: "registry.customers",
	  icon: CircleUserRound,
	  is_hoeppner: true,
	},
  {
    title: t('view.users'),
    url: "registry.users",
    icon: UsersRound,
    is_hoeppner: true,
  },
	{
	  title: t('view.roles'),
	  url: "registry.roles",
	  icon: LockOpen,
	  is_hoeppner: true,
	},
	{
	  title: t('view.standorts'),
	  url: "registry.standorts",
	  icon: MapPin,
	  is_hoeppner: true,
	},
	{
	  title: t('view.functions'),
	  url: "registry.functions",
	  icon: Pyramid,
	  is_hoeppner: true,
	},
	{
	  title: t('view.imports'),
	  url: "registry.imports",
	  icon: Import,
	  is_hoeppner: true,
	},
]);

const isSidebarOpen = ref(true)

function toggleSidebar() {
  isSidebarOpen.value = !isSidebarOpen.value
}


function handleGlobalSearch(search) {
  const newView = "registry." + search.entity.toLowerCase()

  // 1️⃣ Set the view (even if same)
  viewStore.actionView = newView

  // 2️⃣ Force refresh
  viewStore.forceRefresh()
}

console.log("user isexcel=" +  user.value.is_excel)
</script>

<template>
  <Sidebar v-bind="props">
    <SidebarHeader style="padding-bottom: 0">
      <div class="flex items-center space-x-3 border-b" style="padding-left: 15px; padding-right: 15px; padding-bottom: 0">
        <div v-if="isSidebarOpen" class="flex flex-col logo" style="width: 100%; padding-bottom: 15px">
		        <img :src="`/logo/${customer.id}`" alt="Kunde Logo" class="object-contain" style="height: 80px; margin-bottom: 5px" />
		        <div class="customer-product-label" style="font-size: 12px">
			          {{customer.is_premium ? "RK-Comfort" : "RK-Base"}}
		        </div>
            <LanguageSwitcher />
		        <KundeSelect    v-if="user.is_hoeppner" />
		        <StandortSelect v-if="hasManyStandorts"/>
		        <GlobalSearch   v-if="customer.is_premium || user.is_hoeppner" @globalsearch="handleGlobalSearch"/>
        </div>
      </div>
    </SidebarHeader>
    <SidebarContent>
      <NavMain :items="navMain.filter(item => user.is_hoeppner || item.is_authorized)" @updateView="$emit('updateView', $event)" />
    </SidebarContent>
    <SidebarFooter>
      <NavUser :user="data.user" />
	    <SidebarTrigger class="-ml-1"  @click="toggleSidebar" />
    </SidebarFooter>
    <SidebarRail />
  </Sidebar>
</template>

<style>
</style>